import React from 'react';
import { useRecoilValue } from 'recoil';
import { OfferEnquiryForm } from '~/common/components/offer-enquiry-form';
import { submitOfferEnquiry } from '~/common/services/offer-enquiry-service';
import { ActiveSpecialOffer } from '../../../special-offer/store';
import {
  ENQUIRY_ANCHOR_ID,
  PageEnquiryForm,
  RecaptchaSiteKey,
  ShowTestDrivePanel,
  ThankYouUrl,
} from '../../store';
import { TestDrivePanel } from '../test-drive-panel';
import { offerModelName } from '../../model-name';
import styles from './index.module.scss';

/**
 * The band at the foot of the page: the test drive panel on the left, the enquiry form on the right.
 *
 * The panel is deliberately not boxed in - it runs to the left edge of the page and fills the band's height,
 * as the design has it, so the two halves read as one block rather than as a card beside a form.
 *
 * Either half can be absent, which is why there are three renders rather than one:
 *  - No form: the panel stands alone and the page's Enquire button falls back to the separate enquiry page,
 *    so the section is never empty and no offer loses its enquiry path.
 *  - Panel hidden (an offer that is not about a vehicle model - see ShowTestDrivePanel): the form takes the
 *    whole band and centres in the page's content measure instead of in the half it used to occupy.
 *  - Neither: nothing to render, so no empty band and no stray scroll target.
 */
export const OfferEnquirySection: React.FC = () => {
  const offer = useRecoilValue(ActiveSpecialOffer);
  const form = useRecoilValue(PageEnquiryForm);
  const recaptchaSiteKey = useRecoilValue(RecaptchaSiteKey);
  const showPanel = useRecoilValue(ShowTestDrivePanel);
  const thankYouUrl = useRecoilValue(ThankYouUrl);

  // For the analytics event. The make comes from the form's lead settings, which is where the brand is
  // already stated for Salesforce; the model is read from the offer the same way the test drive panel reads
  // it, so the two cannot disagree about which car this offer is about.
  const vehicle = {
    make: form?.leadSettings?.make ?? 'Subaru',
    model: offerModelName(offer?.category, offer?.title) ?? undefined,
  };

  // A full page load rather than a client side route change: this site has no router on the offer page, and
  // the thank you page is server rendered like every other page here.
  const goToThankYou = () => {
    window.location.assign(
      thankYouUrl || `/special-offers/${offer?.uniqueName}/thank-you`
    );
  };

  if (!offer) return null;
  if (!form && !showPanel) return null;

  if (!form) {
    return (
      <section
        id={ENQUIRY_ANCHOR_ID}
        className={styles.section}
        data-testid="offer-enquiry-section"
      >
        <TestDrivePanel />
      </section>
    );
  }

  if (!showPanel) {
    return (
      <section
        id={ENQUIRY_ANCHOR_ID}
        className={styles.section}
        data-testid="offer-enquiry-section"
      >
        <div className={styles.formBand}>
          <OfferEnquiryForm
            form={form}
            offerUniqueName={offer.uniqueName}
            recaptchaSiteKey={recaptchaSiteKey ?? undefined}
            vehicle={vehicle}
            onSubmit={submitOfferEnquiry}
            onSubmitted={goToThankYou}
            className={styles.bandForm}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      id={ENQUIRY_ANCHOR_ID}
      className={styles.section}
      data-testid="offer-enquiry-section"
    >
      <div className={styles.columns}>
        <TestDrivePanel />

        <div className={styles.formColumn}>
          <OfferEnquiryForm
            form={form}
            offerUniqueName={offer.uniqueName}
            recaptchaSiteKey={recaptchaSiteKey ?? undefined}
            vehicle={vehicle}
            onSubmit={submitOfferEnquiry}
            onSubmitted={goToThankYou}
            className={styles.form}
          />
        </div>
      </div>
    </section>
  );
};
